import './index.css'
import { useState } from 'react'
import type { PlanTier, DbSubscription, SubStatus } from '../../types'
import { fmt } from '../../lib/format'

interface PricingPlansProps {
  planTier: PlanTier
  subscription: DbSubscription | null
  loading: boolean
  onUpgrade: (plan: Exclude<PlanTier, 'free'>, cycle: 'monthly' | 'annual') => Promise<void>
  onCancel: () => Promise<void>
}

const STATUS_LABEL: Record<SubStatus, string> = {
  pending:    'Pendente',
  authorized: 'Ativo',
  paused:     'Pausado',
  cancelled:  'Cancelado',
  past_due:   'Pagamento pendente',
}

const PLAN_FEATURES: Record<string, string[]> = {
  free: [
    'Transações ilimitadas',
    '1 cartão de crédito',
    'Histórico de 3 meses',
    '20 interações/mês no bot',
  ],
  pro: [
    'Transações ilimitadas',
    'Cartões ilimitados',
    'Histórico completo',
    'Limites de gasto por categoria',
    'Relatório PDF (em breve)',
    'Insights de IA (em breve)',
  ],
  casal: [
    'Tudo do Pro Individual',
    'Compartilhamento entre casal',
    'Visão consolidada do lar',
  ],
}

interface PlanCardProps {
  tier: PlanTier
  name: string
  monthlyPrice: number | null
  annualPrice: number | null
  current: boolean
  features: string[]
  onSelectMonthly?: () => void
  onSelectAnnual?: () => void
  loading: boolean
  highlight?: boolean
}

function PlanCard({ name, monthlyPrice, annualPrice, current, features, onSelectMonthly, onSelectAnnual, loading, highlight }: PlanCardProps) {
  return (
    <div className={`bc-plan-card${highlight ? ' bc-plan-card--highlight' : ''}${current ? ' bc-plan-card--current' : ''}`}>
      {highlight && <span className="bc-plan-card__badge">Mais popular</span>}
      <h3 className="bc-plan-card__name">{name}</h3>

      {monthlyPrice === null ? (
        <p className="bc-plan-card__price bc-plan-card__price--free">Grátis</p>
      ) : (
        <div className="bc-plan-card__pricing">
          <p className="bc-plan-card__price">{fmt(monthlyPrice)}<span>/mês</span></p>
          {annualPrice && (
            <p className="bc-plan-card__annual">
              {fmt(annualPrice)}/ano{' '}
              <span className="bc-plan-card__save">
                economia de {fmt(monthlyPrice * 12 - annualPrice)}
              </span>
            </p>
          )}
        </div>
      )}

      <ul className="bc-plan-card__features">
        {features.map(f => <li key={f}>{f}</li>)}
      </ul>

      {current ? (
        <span className="bc-plan-card__current-label">Plano atual</span>
      ) : onSelectMonthly ? (
        <div className="bc-plan-card__actions">
          <button className="bc-btn bc-btn--primary bc-btn--full" onClick={onSelectMonthly} disabled={loading}>
            {loading ? '…' : 'Assinar mensal'}
          </button>
          {onSelectAnnual && (
            <button className="bc-btn bc-btn--ghost bc-btn--full" onClick={onSelectAnnual} disabled={loading}>
              {loading ? '…' : 'Assinar anual'}
            </button>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default function PricingPlans({ planTier, subscription, loading, onUpgrade, onCancel }: PricingPlansProps) {
  const [upgrading,    setUpgrading]    = useState(false)
  const [cancelling,   setCancelling]   = useState(false)
  const [upgradeError, setUpgradeError] = useState<string | null>(null)

  async function handleUpgrade(plan: Exclude<PlanTier, 'free'>, cycle: 'monthly' | 'annual') {
    setUpgrading(true)
    setUpgradeError(null)
    try {
      await onUpgrade(plan, cycle)
    } catch (err) {
      setUpgradeError((err as Error).message ?? 'Erro ao iniciar assinatura. Tente novamente.')
      setUpgrading(false)
    }
  }

  async function handleCancel() {
    if (!window.confirm('Cancelar sua assinatura? O acesso Pro permanece até o fim do período pago.')) return
    setCancelling(true)
    try { await onCancel() } finally { setCancelling(false) }
  }

  return (
    <div className="bc-card bc-pricing">
      <div className="bc-list__header" style={{ padding: '16px 20px 0' }}>
        <h3 className="bc-list__title">Planos</h3>
        {subscription && subscription.status !== 'cancelled' && (
          <span className="bc-plan-status">
            {STATUS_LABEL[subscription.status]}
            {subscription.current_period_end && (
              <> · até {new Date(subscription.current_period_end).toLocaleDateString('pt-BR')}</>
            )}
          </span>
        )}
      </div>

      {upgradeError && (
        <div className="bc-pricing__error" role="alert">
          {upgradeError}
        </div>
      )}

      <div className="bc-pricing__grid">
        <PlanCard
          tier="free"
          name="Free"
          monthlyPrice={null}
          annualPrice={null}
          current={planTier === 'free'}
          features={PLAN_FEATURES.free}
          loading={loading || upgrading}
        />
        <PlanCard
          tier="pro"
          name="Pro Individual"
          monthlyPrice={19.90}
          annualPrice={179.90}
          current={planTier === 'pro'}
          features={PLAN_FEATURES.pro}
          onSelectMonthly={() => handleUpgrade('pro', 'monthly')}
          onSelectAnnual={() => handleUpgrade('pro', 'annual')}
          loading={loading || upgrading}
          highlight
        />
        <PlanCard
          tier="casal"
          name="Pro Casal"
          monthlyPrice={29.90}
          annualPrice={269.90}
          current={planTier === 'casal'}
          features={PLAN_FEATURES.casal}
          onSelectMonthly={() => handleUpgrade('casal', 'monthly')}
          onSelectAnnual={() => handleUpgrade('casal', 'annual')}
          loading={loading || upgrading}
        />
      </div>

      {planTier !== 'free' && subscription?.status === 'authorized' && (
        <div style={{ padding: '0 20px 20px', textAlign: 'right' }}>
          <button className="bc-btn bc-btn--ghost bc-btn--sm" onClick={handleCancel} disabled={cancelling}>
            {cancelling ? '…' : 'Cancelar assinatura'}
          </button>
        </div>
      )}
    </div>
  )
}
